import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Checkbox from '../../../components/ui/Checkbox';

const NotificationCard = ({
  notification,
  isSelected,
  onSelect,
  onClick,
  onMarkAsRead,
  onArchive,
  onDelete
}) => {
  const [showActions, setShowActions] = useState(false);

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'booking_confirmation':
        return 'CheckCircle';
      case 'payment_success':
        return 'CreditCard';
      case 'payment_failed': case'payment_alert':
        return 'AlertCircle';
      case 'trip_reminder':
        return 'Calendar';
      case 'booking_update':
        return 'RefreshCw';
      default:
        return 'Bell';
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'booking_confirmation': case'payment_success':
        return 'text-green-600 bg-green-50';
      case 'payment_failed':
        return 'text-red-600 bg-red-50';
      case 'payment_alert':
        return 'text-orange-600 bg-orange-50';
      case 'trip_reminder':
        return 'text-blue-600 bg-blue-50';
      case 'booking_update':
        return 'text-purple-600 bg-purple-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'booking_confirmation':
        return 'Booking Confirmed';
      case 'payment_success':
        return 'Payment Success';
      case 'payment_failed':
        return 'Payment Failed';
      case 'payment_alert':
        return 'Payment Alert';
      case 'trip_reminder':
        return 'Trip Reminder';
      case 'booking_update':
        return 'Booking Update';
      default:
        return 'Notification';
    }
  };

  const getPriorityBadge = (metadata) => {
    const priority = metadata?.priority || 'normal';
    switch (priority) {
      case 'high':
        return (
          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-700">
            High Priority
          </span>
        );
      case 'urgent':
        return (
          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-600 text-white animate-pulse">
            Urgent
          </span>
        );
      default:
        return null;
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '';

    const now = new Date();
    const time = new Date(timestamp);
    const diffInSeconds = Math.floor((now - time) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return time?.toLocaleDateString();
  };

  const handleCardClick = () => {
    onClick?.(notification);
  };

  return (
    <div
      className={`bg-card border rounded-xl p-4 transition-all duration-200 hover:shadow-md ${
        notification?.status === 'unread' ? 'border-primary bg-primary/5' : 'border-border'
      } ${isSelected ? 'ring-2 ring-primary' : ''}`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      <div className="flex items-start gap-4">
        {/* Checkbox */}
        <div className="pt-1">
          <Checkbox
            checked={isSelected}
            onChange={() => onSelect?.(notification?.id)}
          />
        </div>

        {/* Icon */}
        <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${getNotificationColor(notification?.type)}`}>
          <Icon name={getNotificationIcon(notification?.type)} size={24} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4 mb-2">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-medium text-muted-foreground">
                  {getTypeLabel(notification?.type)}
                </span>
                {notification?.status === 'unread' && (
                  <span className="w-2 h-2 rounded-full bg-primary"></span>
                )}
                {getPriorityBadge(notification?.metadata)}
              </div>
              <h3
                className="text-base font-semibold text-foreground mb-1 cursor-pointer hover:text-primary"
                onClick={handleCardClick}
              >
                {notification?.title}
              </h3>
              <p className="text-sm text-muted-foreground line-clamp-2">
                {notification?.message}
              </p>
            </div>

            {/* Timestamp */}
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {formatTimeAgo(notification?.createdAt)}
            </span>
          </div>

          {/* Metadata */}
          {notification?.metadata && Object.keys(notification?.metadata)?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3 text-xs text-muted-foreground">
              {notification?.metadata?.confirmationNumber && (
                <span className="flex items-center gap-1">
                  <Icon name="Hash" size={12} />
                  {notification?.metadata?.confirmationNumber}
                </span>
              )}
              {notification?.metadata?.amount && (
                <span className="flex items-center gap-1">
                  <Icon name="DollarSign" size={12} />
                  ₱{notification?.metadata?.amount?.toLocaleString()}
                </span>
              )}
              {notification?.metadata?.checkIn && (
                <span className="flex items-center gap-1">
                  <Icon name="Calendar" size={12} />
                  {new Date(notification?.metadata?.checkIn)?.toLocaleDateString()}
                </span>
              )}
            </div>
          )}

          {/* Actions */}
          <div className={`flex items-center gap-2 mt-3 transition-opacity duration-200 ${
            showActions ? 'opacity-100' : 'opacity-0 md:opacity-100'
          }`}>
            {notification?.status === 'unread' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e?.stopPropagation();
                  onMarkAsRead?.(notification?.id);
                }}
              >
                <Icon name="Check" size={14} className="mr-1" />
                Mark Read
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e?.stopPropagation();
                onArchive?.(notification?.id);
              }}
            >
              <Icon name="Archive" size={14} className="mr-1" />
              Archive
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e?.stopPropagation();
                if (window.confirm('Delete this notification?')) {
                  onDelete?.(notification?.id);
                }
              }}
              className="text-error hover:text-error"
            >
              <Icon name="Trash2" size={14} className="mr-1" />
              Delete
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationCard;